import React from "react";
import { MarkdownRenderer } from "./MarkdownRenderer.js";
import { EmojiCanvasImg } from "./EmojiCanvasImg.js";
import { useIsMobileWidth } from "./useIsMobileWidth.js";
function noop() {}

export type BannerProps = {
  bgColor: string;
  // bgImage?: string;
  textColor: string;
  // emojiCanvas?: EmojiCanvasImgProps;
  emojis?: string[];
  isAnimationEnabled?: boolean;
  animationTrigger?: "ALWAYS" | "HOVER";
  actionButtonTextColor: string;
  actionButtonBgColor: string;
  primaryText: string;
  isActionEnabled?: boolean;
  isEntireBannerClickable: boolean;
  isActionButtonStyle: boolean;
  isSlim: boolean;
  shouldAllowClose: boolean;
  actionText: string;
  actionUrl: string;
  shouldCacheEmojiCanvas?: boolean;
  isHovered?: boolean;
  forceMobile?: boolean;
  onDismiss?: () => void | Promise<void>;
  onMount?: () => void;
  isPreview?: boolean;
  onClickPreview?: () => void;
};

export function Banner({
  bgColor,
  emojis,
  textColor,
  primaryText,
  isHovered,
  isEntireBannerClickable,
  isAnimationEnabled,
  animationTrigger,
  isActionEnabled,
  actionText,
  actionUrl,
  actionButtonTextColor,
  actionButtonBgColor,
  shouldAllowClose,
  isActionButtonStyle,
  isSlim,
  shouldCacheEmojiCanvas = true,
  isPreview,
  onDismiss = noop,
  onMount = noop,
  onClickPreview = noop,
}: BannerProps) {
  React.useEffect(() => {
    onMount();
  }, []);

  const isEmojiBgEnabled = emojis && emojis.length > 0;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isMobile = useIsMobileWidth({ containerRef });

  return (
    <div
      ref={containerRef}
      data-supabanner-instance
      style={{
        position: "relative",
      }}
    >
      {emojis && (
        <EmojiCanvasImg
          width={300}
          height={300}
          emojis={emojis}
          isHovered={isHovered}
          color1={bgColor}
          shouldCache={shouldCacheEmojiCanvas}
          isAnimationEnabled={isAnimationEnabled}
          animationTrigger={animationTrigger}
        />
      )}
      <div style={{ position: "relative" }}>
        <LinkOrDiv
          isLink={isEntireBannerClickable && isActionEnabled}
          linkProps={{
            target: "_blank",
            href: actionUrl,
            style: { textDecoration: "none" },
            onClick: (e: any) => {
              if (isPreview) {
                e.preventDefault();
                onClickPreview();
              }
            },
            // className: "no-underline",
          }}
        >
          <div
            style={{
              textDecoration: "none",
              position: "relative",
              width: "100%",
              padding: "8px",
              background: isEmojiBgEnabled ? undefined : bgColor,
              // backgroundImage: bgImage ? `url(${bgImage})` : "none",
              color: textColor,
            }}
          >
            <div
              style={{
                maxWidth: "100%",
                marginLeft: "auto",
                marginRight: "auto",
                paddingTop: isSlim ? 0 : "1rem",
                paddingBottom: isSlim ? 0 : "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 6 * 4,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {primaryText && (
                  <BannerMessage
                    primaryText={primaryText}
                    textColor={textColor}
                    bgColor={bgColor}
                    isMobile={isMobile}
                  />
                )}
                {isActionEnabled && actionText && (
                  <BannerAction
                    actionText={actionText}
                    actionUrl={actionUrl}
                    actionButtonTextColor={actionButtonTextColor}
                    actionButtonBgColor={actionButtonBgColor}
                    isActionButtonStyle={isActionButtonStyle}
                    isPreview={isPreview}
                    isEntireBannerClickable={isEntireBannerClickable}
                    onClickPreview={onClickPreview}
                  />
                )}
                {shouldAllowClose && isMobile && (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CloseButton
                      onClick={() => {
                        onDismiss();
                        onClickPreview();
                      }}
                      color={textColor}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </LinkOrDiv>
      </div>

      {shouldAllowClose && !isMobile && (
        <div
          style={{
            position: "absolute",
            right: 20,
            top: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CloseButton onClick={onDismiss} color={textColor} />
        </div>
      )}
    </div>
  );
}

function BannerMessage({
  primaryText,
  textColor,
  bgColor,
  isMobile,
}: {
  primaryText: string;
  textColor: string;
  bgColor: string;
  isMobile: boolean;
}) {
  return (
    <div
      data-supabanner-primary-text
      style={
        {
          // If we wanted to fade out the background behind text
          // boxShadow: `0px 0px 3px 3px ${bgColor}`,
        }
      }
    >
      <MarkdownRenderer
        textColor={textColor}
        bgColor={bgColor}
        isMobile={isMobile}
      >
        {primaryText}
      </MarkdownRenderer>
    </div>
  );
}

function BannerAction({
  actionText,
  actionUrl,
  actionButtonTextColor,
  actionButtonBgColor,
  isActionButtonStyle,
  isEntireBannerClickable,
  isPreview,
  onClickPreview = noop,
}: {
  actionText: string;
  actionUrl: string;
  actionButtonTextColor: string;
  actionButtonBgColor: string;
  isActionButtonStyle: boolean;
  isEntireBannerClickable: boolean;
  onClickPreview: () => void;
  isPreview: boolean;
}) {
  return (
    <div
      data-supabanner-action
      style={{
        flexShrink: 0,
        fontFamily: `-apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Apple Color Emoji', Helvetica, Arial, sans-serif`,
      }}
    >
      <LinkOrDiv
        style={{
          display: "inline-block",
          textDecoration: "none",
          fontWeight: 600,
          fontFamily: `-apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Apple Color Emoji', Helvetica, Arial, sans-serif`,
          // fontSize: "1.125rem",
          // lineHeight: "1.75rem",
          // borderRadius: ".25rem",
          fontSize: "18px",
          lineHeight: "28px",
          borderRadius: 4,
          color: actionButtonTextColor,
          padding: "8px 12px",
          ...(isActionButtonStyle
            ? {
                background: actionButtonBgColor,
              }
            : { textDecoration: "underline" }),
        }}
        isLink={!isEntireBannerClickable}
        linkProps={{
          target: "_blank",
          href: actionUrl,
          onClick: (e: any) => {
            if (isPreview) {
              e.preventDefault();
              onClickPreview();
            }
          },
          style: {
            textDecoration: "none",
            color: actionButtonTextColor,
            ...(isActionButtonStyle
              ? {
                  background: actionButtonBgColor,
                }
              : { textDecoration: "underline" }),
          },
        }}
      >
        {actionText}
      </LinkOrDiv>
    </div>
  );
}

function CloseButton({
  onClick,
  color,
}: {
  onClick: () => void;
  color: string;
}) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      style={{
        fontFamily: `-apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Apple Color Emoji', Helvetica, Arial, sans-serif`,
        color,
        padding: 4,
        background: "transparent",
        fontSize: "1rem",
        cursor: "pointer",
        border: "none",
        // lineHeight: 1.5,
      }}
      // className="px-1 py-1 bg-transparent border-none text-base hover:bg-white/20 flex items-center z-10"
    >
      <svg style={{ fill: color, width: 20, height: 20 }} viewBox="0 0 20 20">
        <path d="M16.707 4.707a1 1 0 0 0-1.414-1.414l-5.293 5.293-5.293-5.293a1 1 0 0 0-1.414 1.414l5.293 5.293-5.293 5.293a1 1 0 1 0 1.414 1.414l5.293-5.293 5.293 5.293a1 1 0 0 0 1.414-1.414l-5.293-5.293 5.293-5.293Z"></path>
      </svg>
    </button>
  );
}

function LinkOrDiv(props: any & { linkProps: { href?: string } }) {
  const {
    linkProps = {},
    divProps = {},
    children,
    isLink,
    style = {},
    ...rest
  } = props;

  if (isLink) {
    return React.createElement(
      "a",
      {
        ...rest,
        ...linkProps,
        style: {
          ...style,
          ...(linkProps.style || {}),
        },
        href: linkProps.href || "#",
      },
      children
    );
  } else {
    return React.createElement(
      "div",
      {
        ...divProps,

        style: {
          ...style,
          ...(divProps.style || {}),
        },
        ...rest,
      },
      children
    );
  }
}
